import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { hasActiveModuleGrant } from '@realadvisor/constants';
import {
  CreditCard,
  ExternalLink,
  Eye,
  GitMerge,
  Search,
  Target,
  Trash2,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { DeletedUser } from '@modules/users/DeletedUser';
import { NOTIFICATIONS_DRAWER_PARAM } from '@modules/users/user-overview/components/UserNotificationsCard';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { UserOverview } from '@modules/users/user-overview/UserOverview';
import { UserBuying } from '@modules/users/UserBuying';
import { UserDeleteDialog } from '@modules/users/UserDeleteDialog';
import { UserMergeDialog } from '@modules/users/UserMergeDialog';
import {
  BUY_APPRAISAL_REASONS,
  GET_USER,
  GET_USER_DRAWER_TAB_COUNTS,
} from '@modules/users/userQueries';
import { UserSelling } from '@modules/users/UserSelling';
import { UserSubscriptions } from '@modules/users/UserSubscriptions';
import { MODULES_BROKERAGE } from '@shared/constants/module';
import { useLocale } from '@shared/hooks/locale';
import { getUUID, toGlobalId } from '@shared/utils/global-id';

type UserDrawerProps = {
  maxWidth?: number;
};

export const UserDrawer = ({ maxWidth = 1200 }: UserDrawerProps) => {
  const navigate = useNavigate();
  const { t, language } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const params = useParams() as { userId: string; '*'?: string };
  const id = getUUID(params.userId);

  const currentTab = (params['*'] ?? '').split('/')[0] || 'overview';

  const isBrokerageEnabled = hasActiveModuleGrant(
    me?.module_grants,
    MODULES_BROKERAGE,
  );
  const canEditSettings = me?.can_edit_settings === true;
  const canViewSubscriptions = useCanViewSubscriptions(id);

  const { data, error } = useQuery(GET_USER, {
    variables: {
      id,
      includeSubscriptions: canViewSubscriptions,
    },
    // Reopening the drawer must reflect teams added/removed elsewhere (e.g.
    // from the team page), so read from cache for instant paint but always
    // revalidate against the network.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  useEffect(() => {
    if (error) {
      setApolloError(error);
    }
  }, [error]);

  const { data: tabCountsData, error: tabCountsError } = useQuery(
    GET_USER_DRAWER_TAB_COUNTS,
    {
      variables: {
        userId: id,
        buyAppraisalReasons: [...BUY_APPRAISAL_REASONS],
      },
      // Same staleness concern as GET_USER above: tab counts must revalidate
      // when the drawer reopens.
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );
  useEffect(() => {
    if (tabCountsError) {
      setApolloError(tabCountsError);
    }
  }, [tabCountsError]);

  const sellingTabCount =
    (tabCountsData?.selling_leads?.aggregate?.count ?? 0) +
    (tabCountsData?.listings?.aggregate?.count ?? 0);
  const buyingTabCount =
    (tabCountsData?.buyer_leads?.aggregate?.count ?? 0) +
    (tabCountsData?.property_searches?.aggregate?.count ?? 0);

  const user = data?.users_by_pk;
  const globalUserId = toGlobalId('User', id);

  const closeDrawer = useCallback(() => {
    const search = new URLSearchParams(searchParams);
    search.delete(NOTIFICATIONS_DRAWER_PARAM);
    navigate({
      pathname: '../',
      search: search.toString(),
    });
  }, [navigate, searchParams]);

  const drawerActions = useMemo(
    () => [
      ...(canEditSettings
        ? [
            {
              label: t('Merge'),
              disabled: user?.is_deleted === true,
              onClick: () => setMergeOpen(true),
              icon: <GitMerge size={16} />,
            },
            {
              label: t('Delete'),
              disabled: user?.is_deleted === true,
              onClick: () => setDeleteOpen(true),
              icon: <Trash2 size={16} />,
            },
          ]
        : []),
      {
        label: t('Open legacy view'),
        icon: <ExternalLink size={16} />,
        onClick: () =>
          window.open(
            `/${language}/v1/users/${globalUserId}`,
            '_blank',
            'noopener,noreferrer',
          ),
      },
    ],
    [globalUserId, canEditSettings, language, t, user?.is_deleted],
  );

  const shouldShowTab = useCallback(
    (value: string) => {
      if (value === 'buying') {
        return isBrokerageEnabled;
      }
      if (value === 'subscriptions') {
        return me?.is_admin === true;
      }
      return true;
    },
    [isBrokerageEnabled, me?.is_admin],
  );

  const tabs: DrawerTab[] = useMemo(() => {
    const tabsData: [string, string, React.ReactElement?][] = [
      [t('Overview'), 'overview', <Eye size={18} />],
      [t('Selling'), 'selling', <Target size={18} />],
      [t('Buying'), 'buying', <Search size={18} />],
      [t('Subscriptions'), 'subscriptions', <CreditCard size={18} />],
    ];

    return tabsData
      .filter(([, value]) => shouldShowTab(value))
      .map(([label, value, icon]) => ({
        ...generateTab([searchParams.toString(), label, value, icon]),
        badge:
          value === 'selling'
            ? sellingTabCount
            : value === 'buying'
            ? buyingTabCount
            : undefined,
      }));
  }, [t, shouldShowTab, searchParams, sellingTabCount, buyingTabCount]);

  return (
    <Drawer
      maxWidth={maxWidth}
      onClose={closeDrawer}
      title={[t('User'), user?.full_name].filter(Boolean).join(': ')}
      actions={drawerActions}
      tabs={tabs}
      currentTab={currentTab}
    >
      {user?.is_deleted && <DeletedUser />}
      <Routes>
        <Route path="overview" element={<UserOverview user={user} />} />
        <Route path="selling/*" element={<UserSelling userId={id} />} />
        {shouldShowTab('buying') && (
          <Route path="buying/*" element={<UserBuying userId={id} />} />
        )}
        {shouldShowTab('subscriptions') && (
          <Route
            path="subscriptions"
            element={<UserSubscriptions userId={id} />}
          />
        )}
        <Route
          path="*"
          element={
            <Navigate
              replace
              to={{
                pathname: './overview',
                search: searchParams.toString(),
              }}
            />
          }
        />
      </Routes>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />

      <UserMergeDialog
        leftUserId={id}
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false);
          closeDrawer();
        }}
      />

      <UserDeleteDialog
        userId={id}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          closeDrawer();
        }}
      />
    </Drawer>
  );
};
